import { COLLECT_SAVE_TARGETS, WORKFLOW_LIMITS, type CollectSaveTarget } from "@sagot/constants";
import type { CollectOption } from "../types";

const KEYED_TARGETS = ["lead", "var"] as const;
const KEY_INVALID = /[^a-z0-9_]+/g;
const OPTION_ID_MAX = 40;
const DIACRITICS = /[̀-ͯ]/g;

export interface SaveTarget {
  target: CollectSaveTarget;
  /** The `<key>` of `lead.<key>` / `var.<key>`; empty for the visitor fields. */
  key: string;
}

/** `visitor.email` → `visitor_email`: a dot in a catalog key would nest it. */
export const targetKey = (target: string): string => target.replace(".", "_");

export const isKeyedTarget = (target: CollectSaveTarget): target is (typeof KEYED_TARGETS)[number] => (KEYED_TARGETS as readonly string[]).includes(target);

/** `visitor.email` / `lead.grade_level` / `var.answer` as a target + key. */
export function saveTargetOf(saveTo: string): SaveTarget {
  const fixed = COLLECT_SAVE_TARGETS.find((target) => target === saveTo);
  if (fixed) return { target: fixed, key: "" };
  for (const target of KEYED_TARGETS) {
    if (saveTo.startsWith(`${target}.`)) return { target, key: saveTo.slice(target.length + 1) };
  }
  return { target: "var", key: "" };
}

export function saveToOf({ target, key }: SaveTarget): string {
  return isKeyedTarget(target) ? `${target}.${key}` : target;
}

/** A key the server accepts: lower-case letters, digits and `_`. */
export function sanitizeKey(raw: string): string {
  return raw.toLowerCase().replace(KEY_INVALID, "_").slice(0, WORKFLOW_LIMITS.customKey);
}

function slugOf(label: string): string {
  const slug = label.normalize("NFKD").replace(DIACRITICS, "").toLowerCase().replace(KEY_INVALID, "_").replace(/^_+|_+$/g, "");
  return slug.slice(0, OPTION_ID_MAX);
}

/** One choice per line (blank lines ignored, duplicates dropped); ids stay stable for unchanged labels. */
export function parseOptions(text: string, previous: readonly CollectOption[] = []): CollectOption[] {
  const known = new Map(previous.map((option) => [option.label, option.id]));
  const taken = new Set<string>();
  const options: CollectOption[] = [];
  for (const line of text.split("\n")) {
    const label = line.trim().slice(0, WORKFLOW_LIMITS.fieldLabel);
    if (!label || options.some((option) => option.label === label)) continue;
    const base = known.get(label) ?? (slugOf(label) || `option_${String(options.length + 1)}`);
    let id = base;
    for (let n = 2; taken.has(id); n += 1) id = `${base}_${String(n)}`;
    taken.add(id);
    options.push({ id, label });
    if (options.length === WORKFLOW_LIMITS.fieldOptions) break;
  }
  return options;
}

export const optionsText = (options: readonly CollectOption[] = []): string => options.map((option) => option.label).join("\n");
